const W = 640;
const H = 200;
const PAD = 28;
const DASHES = [undefined, '6 4', '2 3', '10 3 2 3'];

export interface CompareSeries {
  label: string;
  /** One value per day; null leaves a gap (a rate without sends that day). */
  values: Array<number | null>;
}

const show = (format: 'count' | 'percent', v: number) => (format === 'percent' ? `${(v * 100).toFixed(1)} %` : v.toLocaleString('en-US'));

/**
 * Groups of a channel dashboard as lines over the same days (BB-134): transactional vs marketing, campaigns or
 * platforms. Lines differ by dash pattern as well as by order in the legend, so they read without colour.
 */
export function CompareChart({ title, days, series, format }: { title: string; days: string[]; series: CompareSeries[]; format: 'count' | 'percent' }) {
  const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const max = Math.max(format === 'percent' ? 0.01 : 1, ...all);
  const x = (i: number) => PAD + (days.length > 1 ? i / (days.length - 1) : 0.5) * (W - 2 * PAD);
  const y = (v: number) => H - PAD - (v / max) * (H - 2 * PAD);
  const path = (values: Array<number | null>) => values.map((v, i) => (v === null ? '' : `${i && values[i - 1] !== null ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)).join(' ');
  const total = (s: CompareSeries) => {
    const known = s.values.filter((v): v is number => v !== null);
    return known.length ? known[known.length - 1]! : null;
  };
  const summary = series.map((s) => `${s.label}: last ${total(s) === null ? 'no data' : show(format, total(s)!)}`).join('; ');
  return (
    <figure className="home-chart">
      <figcaption>
        {title}
        <span className="home-chart__legend">
          {series.map((s, i) => (
            <span key={s.label} className={i ? 'home-chart__key home-chart__key--dashed' : 'home-chart__key'}>
              {s.label}
            </span>
          ))}
        </span>
      </figcaption>
      {days.length > 0 && (
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}, ${days[0]} to ${days.at(-1)}: ${summary}`}>
          {series.map((s, i) => (
            <path key={s.label} d={path(s.values)} fill="none" stroke="currentColor" strokeWidth={2} strokeDasharray={DASHES[i % DASHES.length]}>
              <title>{s.label}</title>
            </path>
          ))}
          <text x={PAD} y={H - 8} fontSize={11}>
            {days[0]}
          </text>
          <text x={W - PAD} y={H - 8} fontSize={11} textAnchor="end">
            {days.at(-1)}
          </text>
          <text x={PAD} y={14} fontSize={11}>
            {show(format, max)}
          </text>
        </svg>
      )}
    </figure>
  );
}
